import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, X } from '../bravosIcons';
import type { WorkoutSummary } from '../../../types/dataModel';
import {
  filterLibraryWorkouts,
  topFocusTagChips,
} from '../utils/workoutLibraryFilters';
import '../styles/trainer-calendar-tokens.css';
import '../styles/trainer-action-tokens.css';
import '../styles/trainer-action-primitives.css';
import '../styles/trainer-dialog-tokens.css';
import '../styles/trainer-dialog-primitives.css';
import './TrainerWorkoutLibraryDrawer.css';

export interface TrainerWorkoutLibraryDrawerProps {
  open: boolean;
  title?: string;
  contextLine: React.ReactNode;
  workouts: WorkoutSummary[];
  loading?: boolean;
  busy?: boolean;
  onClose: () => void;
  onCreateWorkout: () => void;
  onAssign: (workout: WorkoutSummary) => void | Promise<void>;
}

/**
 * Shared Add Workout library drawer — identical chrome for Member Detail + Plan builder.
 * Markup/classes match Member Detail’s `#238` workout-assign drawer.
 */
export function TrainerWorkoutLibraryDrawer({
  open,
  title = 'Add a workout',
  contextLine,
  workouts,
  loading = false,
  busy = false,
  onClose,
  onCreateWorkout,
  onAssign,
}: TrainerWorkoutLibraryDrawerProps) {
  const [search, setSearch] = useState('');
  const [focusTag, setFocusTag] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setFocusTag(null);
    setSelectedId('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const chips = useMemo(() => topFocusTagChips(workouts, 5), [workouts]);
  const filtered = useMemo(
    () => filterLibraryWorkouts(workouts, search, focusTag),
    [workouts, search, focusTag],
  );

  if (!open) return null;

  const handleAssign = () => {
    const workout = workouts.find((w) => w.id === selectedId);
    if (!workout || busy) return;
    void onAssign(workout);
  };

  return createPortal(
    <div className="member-detail-overlays tr-workout-lib-overlays">
      <div
        className="md-scrim"
        onClick={() => {
          if (!busy) onClose();
        }}
        aria-hidden
      />
      <aside
        className="md-drawer open md-lib-drawer md-workout-assign-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="md-add-workout-title"
      >
        <div className="md-drawer-header md-lib-header">
          <div className="md-lib-header-top">
            <div>
              <div className="md-drawer-eyebrow">
                <span className="dot" />
                From your library
              </div>
              <div className="md-drawer-title" id="md-add-workout-title">
                {title}
              </div>
            </div>
            <button
              type="button"
              className="tr-dialog__close md-workout-assign-close"
              aria-label="Close"
              onClick={onClose}
              disabled={busy}
            >
              <X width={16} height={16} aria-hidden />
            </button>
          </div>
          <div className="md-lib-context md-workout-assign-context">{contextLine}</div>
          <div className="md-assign-header-divider" aria-hidden />
          <div className="md-lib-search">
            <Search width={16} height={16} aria-hidden />
            <input
              type="search"
              placeholder="Search workouts, muscles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              disabled={busy}
            />
          </div>
          <div className="md-lib-chips" role="toolbar" aria-label="Filter by muscle focus">
            <button
              type="button"
              className={`md-lib-chip${!focusTag ? ' active' : ''}`}
              onClick={() => setFocusTag(null)}
              disabled={busy}
            >
              All
            </button>
            {chips.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`md-lib-chip${focusTag === tag ? ' active' : ''}`}
                onClick={() => setFocusTag((prev) => (prev === tag ? null : tag))}
                disabled={busy}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        <div className="md-drawer-body md-lib-body">
          <div className="md-lib-count">
            {loading
              ? 'Loading…'
              : `${filtered.length} workout${filtered.length === 1 ? '' : 's'}`}
          </div>
          {!loading && workouts.length === 0 ? (
            <div className="md-lib-empty">
              <strong>No workouts in your library yet.</strong>
              <br />
              Create a workout to assign it here.
            </div>
          ) : !loading && filtered.length === 0 ? (
            <div className="md-lib-empty">
              <strong>No matches.</strong>
              <br />
              Try a different search or filter.
            </div>
          ) : (
            filtered.map((w) => {
              const selected = selectedId === w.id;
              return (
                <button
                  key={w.id}
                  type="button"
                  className={`md-lib-item md-workout-assign-item${selected ? ' selected' : ''}`}
                  disabled={busy}
                  onClick={() => setSelectedId(w.id)}
                >
                  <div className="md-lib-item-name">{w.name}</div>
                  {(w.focusTags?.length ?? 0) > 0 ? (
                    <div className="md-lib-item-tags">
                      {w.focusTags!.slice(0, 3).map((t, idx) => (
                        <span key={t} className={`md-lib-tag${idx === 0 ? ' muscle' : ''}`}>
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="md-lib-item-meta tnum">
                    <span>
                      {w.exerciseCount} exercise{w.exerciseCount === 1 ? '' : 's'}
                    </span>
                    <span className="md-dotsep" aria-hidden />
                    <span>{w.estimatedDurationMinutes ?? 0} min</span>
                  </div>
                </button>
              );
            })
          )}
        </div>
        <div className="md-drawer-footer md-lib-footer md-assign-footer">
          <button
            type="button"
            className="md-lib-create"
            onClick={onCreateWorkout}
            disabled={busy}
          >
            <Plus width={15} height={15} aria-hidden />
            Create new workout
          </button>
          <button
            type="button"
            className="md-btn-primary"
            disabled={busy || !selectedId || filtered.length === 0}
            onClick={handleAssign}
          >
            {busy ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
